import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, texte, useMessageTraduit } from '../utils/messageTraduit';
import { fileUrl } from '../services/api';
import {
  updateMyCompany,
  uploadCompanyLogo,
  deleteCompanyLogo,
} from '../services/companyService';

/**
 * Écran « Ma société » — onglet d'`/admin`, réservé à l'ADMIN (lot 6e-iii).
 *
 * ── Pourquoi un COMPOSANT et pas du JSX dans Admin.js ───────────────────────
 * `Admin.js` fait déjà 727 lignes et porte deux écrans complets (utilisateurs,
 * stocks) avec leurs dialogues. Y ajouter un troisième formulaire de 8 champs
 * plus la gestion d'un fichier le pousserait à ~1000 lignes, et les trois
 * états de formulaire cohabiteraient dans le même composant — c'est ainsi
 * qu'on finit par écrire `setFormData` là où il fallait `setStockFormData`.
 *
 * ── 🔴 LE PAYLOAD SE CONSTRUIT CHAMP PAR CHAMP ─────────────────────────────
 * `Company.to_dict()` rend **18 clés** ; `PUT /admin/company` en accepte **8**
 * et refuse les autres en **400** — dont `plan`, `status`, les quotas et
 * `logo_path`. Un `{...company}` ferait donc échouer CHAQUE enregistrement,
 * avec un message parlant d'abonnement à quelqu'un qui corrigeait son adresse.
 * `SuperAdminConsole.js` construit déjà son payload de la même façon, pour la
 * même raison : ce n'est pas une précaution locale, c'est la règle de la
 * maison.
 *
 * ── LA VALIDATION N'EST PAS DUPLIQUÉE ICI ──────────────────────────────────
 * L'énoncé des règles est affiché (`helperText`), la RÈGLE elle-même reste au
 * serveur. C'est la ligne tenue depuis la politique de mot de passe : deux
 * validations divergent au premier changement, et c'est toujours celle du
 * front qui ment. Seul le plafond du logo est vérifié avant l'envoi — pour
 * éviter de pousser 8 Mo sur le réseau et se les faire refuser, jamais pour
 * décider à la place du serveur.
 *
 * ── LES MESSAGES DU SERVEUR S'AFFICHENT TELS QUELS ─────────────────────────
 * Ils nomment la clé fautive et disent POURQUOI — c'est tout l'objet du refus
 * bruyant construit en 6e-i. Les remplacer par « Erreur d'enregistrement »
 * gaspillerait le seul travail qui rend ce refus utile.
 */

/** Les 8 champs écrivables, dans l'ordre où le serveur les valide. */
const CHAMPS = [
  'name',
  'registration_number',
  'billing_address',
  'billing_country',
  'representative_first_name',
  'representative_last_name',
  'representative_email',
  'representative_phone',
];

/**
 * Plafond du logo, en octets. DUPLIQUE `_TAILLE_MAX_LOGO` (backend
 * routes/admin.py) — duplication ASSUMÉE parce qu'une dérive serait VISIBLE
 * dans les deux sens : trop strict ici, l'utilisateur voit un refus que le
 * serveur n'aurait pas fait ; trop laxiste, il voit le 400 du serveur, avec
 * son message. Aucun des deux cas n'est silencieux. Ce contrôle n'existe que
 * pour éviter un aller-retour réseau inutile.
 */
const TAILLE_MAX_LOGO = 2 * 1024 * 1024;

const depuisSociete = (company) =>
  CHAMPS.reduce((acc, cle) => ({ ...acc, [cle]: company?.[cle] || '' }), {});

const MaSociete = ({ company, onCompanyChange, showSnackbar }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(() => depuisSociete(company));
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useMessageTraduit();
  const [logoOccupe, setLogoOccupe] = useState(false);

  const majChamp = (cle) => (e) => setForm({ ...form, [cle]: e.target.value });

  /**
   * Extrait le message du serveur, tel qu'il l'a écrit.
   * L'intercepteur d'`api.js` rejette l'erreur axios brute : le corps est donc
   * dans `response.data`, avec la clé `error` et, selon le cas, `champ` ou
   * `cle_refusee`. On n'ajoute rien et on ne reformule pas.
   */
  /**
   * 🔴 REND UN DESCRIPTEUR, PLUS UNE CHAÎNE — sinon le repli traduit serait figé.
   * `messageServeur(err, 'company.saveError')` évaluait `t()` **au moment de
   * l'erreur** : la clé brute restait gelée si le dictionnaire n'était pas
   * encore arrivé. Le repli est désormais une CLÉ, traduite au rendu.
   * ⚠️ `err.message` reste du TEXTE : une exception JavaScript n'a pas de clé
   * de traduction, et la rendre comme telle afficherait la phrase entière comme
   * un identifiant introuvable.
   */
  const messageServeur = (err, cleDeRepli) => {
    const duServeurOuLexception = err?.response?.data?.error || err?.message;
    return duServeurOuLexception ? texte(duServeurOuLexception) : cle(cleDeRepli);
  };

  const enregistrer = async () => {
    setErreur(null);
    setEnregistrement(true);
    try {
      // ⚠️ Champ par champ. Voir l'en-tête du fichier : jamais `{...company}`.
      const payload = {};
      CHAMPS.forEach((cle) => {
        payload[cle] = form[cle].trim();
      });
      const { company: maj } = await updateMyCompany(payload);
      onCompanyChange(maj);
      setForm(depuisSociete(maj));
      showSnackbar(t('company.saved'), 'success');
    } catch (err) {
      setErreur(messageServeur(err, 'company.saveError'));
    } finally {
      setEnregistrement(false);
    }
  };

  const choisirLogo = async (e) => {
    const fichier = e.target.files?.[0];
    // Le champ est remis à zéro pour que re-choisir LE MÊME fichier après un
    // refus déclenche bien un nouvel événement.
    e.target.value = '';
    if (!fichier) return;

    setErreur(null);
    if (fichier.size > TAILLE_MAX_LOGO) {
      // Économie d'un aller-retour, pas une décision : le serveur refuserait
      // de toute façon, avec ce même plafond.
      setErreur(cle('company.logoTooLarge', {
        max: Math.round(TAILLE_MAX_LOGO / (1024 * 1024)),
        size: (fichier.size / (1024 * 1024)).toFixed(2),
      }));
      return;
    }

    setLogoOccupe(true);
    try {
      const { company: maj } = await uploadCompanyLogo(fichier);
      onCompanyChange(maj);
      showSnackbar(t('company.logoSaved'), 'success');
    } catch (err) {
      setErreur(messageServeur(err, 'company.logoError'));
    } finally {
      setLogoOccupe(false);
    }
  };

  const retirerLogo = async () => {
    setErreur(null);
    setLogoOccupe(true);
    try {
      const { company: maj } = await deleteCompanyLogo();
      onCompanyChange(maj);
      showSnackbar(t('company.logoRemoved'), 'success');
    } catch (err) {
      setErreur(messageServeur(err, 'company.logoError'));
    } finally {
      setLogoOccupe(false);
    }
  };

  const champ = (cle, extra = {}) => (
    <TextField
      fullWidth
      label={t(`company.${cle}`)}
      value={form[cle]}
      onChange={majChamp(cle)}
      margin="normal"
      {...extra}
    />
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('company.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('company.intro')}
      </Typography>

      {erreur && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}

      {/* ─── Le logo ─────────────────────────────────────────────────── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* ⚠️ `company.logo` vaut « Logo » en fr ET en en — ce n'est PAS un
            oubli de traduction, c'est un mot international. Ne pas le
            « corriger » : un contrôle de complétude i18n qui signalerait les
            valeurs identiques d'une langue à l'autre produirait ici un faux
            positif. (L'hébreu, lui, porte bien « לוגו ».) */}
        <Typography variant="subtitle1" gutterBottom>
          {t('company.logo')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('company.logoHint', { max: Math.round(TAILLE_MAX_LOGO / (1024 * 1024)) })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Avatar
            src={company?.logo_path ? fileUrl(company.logo_path) : undefined}
            variant="rounded"
            sx={{ width: 120, height: 66, bgcolor: 'grey.200' }}
          >
            {!company?.logo_path && <PhotoCameraIcon color="disabled" />}
          </Avatar>
          <Button
            component="label"
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            disabled={logoOccupe}
          >
            {company?.logo_path ? t('company.logoReplace') : t('company.logoUpload')}
            <input
              type="file"
              hidden
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={choisirLogo}
            />
          </Button>
          {company?.logo_path && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={retirerLogo}
              disabled={logoOccupe}
            >
              {t('company.logoRemove')}
            </Button>
          )}
          {logoOccupe && <CircularProgress size={22} />}
        </Box>
      </Paper>

      {/* ─── Identité et facturation ─────────────────────────────────── */}
      <Paper sx={{ p: 3 }}>
        {champ('name', { required: true, helperText: t('company.nameHint') })}
        {champ('registration_number', {
          helperText: t('company.registrationNumberHint'),
        })}
        {champ('billing_address', { multiline: true, rows: 3 })}
        {/* ⚠️ La contrainte est ÉNONCÉE, pas revalidée ici : l'utilisateur ne
            doit pas découvrir la règle par un refus, et le serveur reste seul
            juge. Il normalise la casse et les espaces — « fr » est accepté. */}
        {champ('billing_country', { helperText: t('company.billingCountryHint') })}

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" gutterBottom>
          {t('company.representative')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('company.representativeHint')}
        </Typography>
        {champ('representative_first_name')}
        {champ('representative_last_name')}
        {champ('representative_email', { type: 'email' })}
        {champ('representative_phone')}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={enregistrement ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={enregistrer}
            disabled={enregistrement}
          >
            {t('company.save')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default MaSociete;
